import React, { useContext, useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Tabs, 
  Tab, 
  Typography, 
  Grid, 
  TextField, 
  InputAdornment, 
  Paper,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Alert
} from '@mui/material';
import axios from '../../utils/axios.utils';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useLocation } from 'react-router-dom';
import DataTable from '../../dataTable/DataTable/DataTable';
import TabPanel from '../../shared/tabs/TabPanel';
import { ManufacturersContext } from '../contexts/ManufacturersContext';
import { MfrRecipesProvider, useMfrRecipesContext } from '../contexts/MfrRecipesContext';
import { MfrLiquidsProvider, useMfrLiquidsContext } from '../contexts/MfrLiquidsContext';
import { MfrSeedsProvider, useMfrSeedsContext } from '../contexts/MfrSeedsContext';
import { MfrGTINPrefixesProvider, useMfrGTINPrefixesContext } from '../contexts/MfrGTINPrefixesContext';
import manufacturerColumns from '../constants/manufacturerColumns';
import mfrRecipeColumns from '../constants/mfrRecipeColumns';
import liquidColumns from '../constants/mfrLiquidColumns';
import seedColumns from '../constants/mfrSeedColumns';
import gtinPrefixColumns from '../constants/mfrGTINPrefixColumns';
import NewManufacturerForm from '../components/NewManufacturerForm';
import NewMfrRecipeForm from '../components/NewMfrRecipeForm';
import NewMfrSeedForm from '../components/NewMfrSeedForm';
import NewMfrLiquidForm from '../components/NewMfrLiquidForm';
import NewGtinPrefixForm from '../components/NewGtinPrefixForm';
import EditManufacturerForm from '../components/EditManufacturerForm';
import EditMfrRecipeForm from '../components/EditMfrRecipeForm';
import EditMfrSeedForm from '../components/EditMfrSeedForm';
import EditMfrLiquidForm from '../components/EditMfrLiquidForm';
import EditGtinPrefixForm from '../components/EditGtinPrefixForm';

const AdminManufacturerListPage = () => {
  const [openDialogType, setOpenDialogType] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const dialogFormTypes = ['manufacturer', 'recipe', 'seed', 'liquid', 'gtin_prefix'];
  const { fetchManufacturers, manufacturers, manufacturersLoading } = useContext(ManufacturersContext);
  
  const handleDeleteClick = (item: any) => {
    console.log('Delete clicked for item:', item);
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      // Determine the correct ID based on the active tab
      let idToDelete = '';
      let deleteEndpoint = '';
      
      switch (activeTab) {
        case 0: // Manufacturers
          idToDelete = itemToDelete.autotreat_mfr_cloud_id;
          deleteEndpoint = 'api/admin/autotreat_mfr/mfrs';
          break;
        case 1: // Treatment Recipes
          idToDelete = itemToDelete.autotreat_mfr_recipe_cloud_id;
          deleteEndpoint = 'api/admin/autotreat_mfr/mfr_recipes';
          break;
        case 2: // Seeds
          idToDelete = itemToDelete.autotreat_mfr_seed_cloud_id;
          deleteEndpoint = 'api/admin/autotreat_mfr/mfr_seeds';
          break;
        case 3: // Treatment Products
          idToDelete = itemToDelete.autotreat_mfr_liquid_cloud_id;
          deleteEndpoint = 'api/admin/autotreat_mfr/mfr_liquids';
          break;
        case 4: // GTIN Prefixes
          idToDelete = itemToDelete.autotreat_gtin_prefix_cloud_id;
          deleteEndpoint = 'api/admin/autotreat_mfr/gtin_prefixes';
          break;
        default:
          throw new Error('Invalid tab');
      }

      if (!idToDelete) {
        throw new Error('Item ID not found');
      }

      await axios.delete(`${deleteEndpoint}/${idToDelete}/`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
        }
      });
      
      showSnackbar('Item deleted successfully', 'success');
      
      // Refresh the appropriate data based on active tab
      if (activeTab === 0) fetchManufacturers();
      else if (activeTab === 1) fetchRecipes();
      else if (activeTab === 2) fetchSeeds();
      else if (activeTab === 3) fetchLiquids();
      else if (activeTab === 4) fetchGTINPrefixes();
      
    } catch (error: any) {
      console.error('Error deleting item:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to delete item. Please try again.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };
  
  // Enhance data with delete handler
  const enhancedManufacturers = useMemo(() => {
    return manufacturers.map(item => ({
      ...item,
      onDelete: handleDeleteClick
    }));
  }, [manufacturers]);
  const dialogTitles = ['New Manufacturer', 'New Treatment Recipe', 'New Seed', 'New Treatment Product', 'New GTIN Prefix'];
  const { fetchRecipes, recipes, recipesLoading } = useMfrRecipesContext();
  const { liquids, liquidsLoading, fetchLiquids } = useMfrLiquidsContext();
  const { seeds, seedsLoading, fetchSeeds } = useMfrSeedsContext();
  const { gtinPrefixes, gtinPrefixesLoading, fetchGTINPrefixes } = useMfrGTINPrefixesContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialTab = parseInt(searchParams.get('activeTab') || '0', 10);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerms, setSearchTerms] = useState(['', '', '', '', '']);

  useEffect(() => {
    if (activeTab === 0 && !manufacturers.length) fetchManufacturers();
    else if (activeTab === 1 && !recipes.length) fetchRecipes();
    else if (activeTab === 2 && !seeds.length) fetchSeeds();
    else if (activeTab === 3 && !liquids.length) fetchLiquids();
    else if (activeTab === 4 && !gtinPrefixes.length) fetchGTINPrefixes();
  }, [activeTab]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    const newParams = new URLSearchParams(location.search);
    newParams.set('activeTab', String(newValue));
    window.history.replaceState({}, '', `${location.pathname}?${newParams.toString()}`);
  };

  const handleSearchChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerms = [...searchTerms];
    newSearchTerms[index] = event.target.value;
    setSearchTerms(newSearchTerms);
  };

  const filteredData = useMemo(() => {
    const searchData = (data: any[], searchTerm: string, columns: any[]) => {
      if (!searchTerm) return data;
      const lowercasedSearch = searchTerm.toLowerCase();
      
      return data.filter(row => {
        if (!row) return false;
        
        // Skip the onDelete property when searching
        const rowData = { ...row };
        delete rowData.onDelete;
        
        // Special handling for recipe search to include liquid_recipe_name
        if (row.liquid_recipe_name && 
            String(row.liquid_recipe_name).toLowerCase().includes(lowercasedSearch)) {
          return true;
        }
        
        // If no columns provided, search through all string/number properties of the row
        if (!columns || columns.length === 0) {
          return Object.values(rowData).some(value => 
            (typeof value === 'string' || typeof value === 'number') && 
            String(value).toLowerCase().includes(lowercasedSearch)
          );
        }
        
        // If columns are provided, search through specified columns
        return columns.some(column => {
          if (!column.selector) return false;
          
          // Handle both function and string selectors
          let value;
          if (typeof column.selector === 'function') {
            try {
              value = column.selector(row);
            } catch (e) {
              console.warn('Error executing selector:', e);
              return false;
            }
          } else if (typeof column.selector === 'string') {
            // Handle string selectors (including nested properties)
            value = column.selector.split('.').reduce((obj, key) => 
              obj && obj[key] !== undefined ? obj[key] : '', row);
          } else {
            return false;
          }
          
          // Convert the value to string and check for match
          return String(value || '').toLowerCase().includes(lowercasedSearch);
        });
      });
    };

    return [
      searchData(manufacturers, searchTerms[0], manufacturerColumns),
      searchData(recipes, searchTerms[1], mfrRecipeColumns),
      searchData(seeds, searchTerms[2], seedColumns),
      searchData(liquids, searchTerms[3], liquidColumns),
      searchData(gtinPrefixes, searchTerms[4], gtinPrefixColumns),
    ];
  }, [manufacturers, recipes, seeds, liquids, gtinPrefixes, searchTerms, manufacturerColumns, mfrRecipeColumns, seedColumns, liquidColumns, gtinPrefixColumns]);

  const handleAddButtonClick = () => {
    setOpenDialogType(dialogFormTypes[activeTab]);
  };

  const theme = useTheme();

  const handleCloseDialog = () => {
    setOpenDialogType(null);
    setEditingItem(null);
    setIsEditing(false);
  };

  const handleEditItem = (item: any) => {
    setEditingItem(item);
    setIsEditing(true);
    setOpenDialogType(dialogFormTypes[activeTab]);
  };

  const handleFormSubmitSuccess = async () => {
    const currentFormType = openDialogType;
    setOpenDialogType(null); // Close dialog first

    // Trigger data refresh based on the submitted form type
    if (currentFormType === 'manufacturer' && fetchManufacturers) await fetchManufacturers();
    if (currentFormType === 'recipe' && fetchRecipes) await fetchRecipes();
    if (currentFormType === 'seed' && fetchSeeds) await fetchSeeds();
    if (currentFormType === 'liquid' && fetchLiquids) await fetchLiquids();
    if (currentFormType === 'gtin_prefix' && fetchGTINPrefixes) await fetchGTINPrefixes();
  };

  const renderDialogContent = () => {
    const commonProps = {
      onSuccess: () => {
        // Refresh the data when the form is submitted successfully
        if (activeTab === 0) fetchManufacturers();
        else if (activeTab === 1) fetchRecipes();
        else if (activeTab === 2) fetchSeeds();
        else if (activeTab === 3) fetchLiquids();
        else if (activeTab === 4) fetchGTINPrefixes();
        handleCloseDialog();
      },
      showSnackbar: showSnackbar,
    };

    if (isEditing && editingItem) {
      const editProps = {
        ...commonProps,
        id: editingItem.id || editingItem.autotreat_mfr_cloud_id || 
            editingItem.autotreat_mfr_recipe_cloud_id || 
            editingItem.autotreat_mfr_seed_cloud_id ||
            editingItem.autotreat_mfr_liquid_cloud_id ||
            editingItem.autotreat_gtin_prefix_cloud_id,
        initialData: editingItem
      };

      switch (openDialogType) {
        case 'manufacturer':
          return <EditManufacturerForm {...editProps} />;
        case 'recipe':
          return <EditMfrRecipeForm {...editProps} />;
        case 'seed':
          return <EditMfrSeedForm {...editProps} />;
        case 'liquid':
          return <EditMfrLiquidForm {...editProps} />;
        case 'gtin_prefix':
          return <EditGtinPrefixForm {...editProps} />;
        default:
          return null;
      }
    } else {
      // Add new item
      switch (openDialogType) {
        case 'manufacturer':
          return <NewManufacturerForm {...commonProps} />;
        case 'recipe':
          return <NewMfrRecipeForm {...commonProps} />;
        case 'seed':
          return <NewMfrSeedForm {...commonProps} />;
        case 'liquid':
          return <NewMfrLiquidForm {...commonProps} />;
        case 'gtin_prefix':
          return <NewGtinPrefixForm {...commonProps} />;
        default:
          return null;
      }
    }
  };

  const currentDialogTitle = openDialogType ? dialogTitles[dialogFormTypes.indexOf(openDialogType)] : '';

  // Use enhanced data for manufacturers with search filtering
  const displayData = useMemo(() => {
    const data = [...filteredData];
    if (activeTab === 0) {
      // For manufacturers, we need to apply the search filter to the enhanced manufacturers
      const searchTerm = searchTerms[0].toLowerCase();
      if (searchTerm) {
        const filteredManufacturers = enhancedManufacturers.filter(manufacturer => 
          Object.values(manufacturer).some(value => 
            (typeof value === 'string' || typeof value === 'number') && 
            String(value).toLowerCase().includes(searchTerm)
          )
        );
        data[0] = filteredManufacturers;
      } else {
        data[0] = enhancedManufacturers;
      }
    }
    return data;
  }, [filteredData, enhancedManufacturers, activeTab, searchTerms]);

  return (
    <Box sx={{ p: 3 }}>
      <MfrRecipesProvider>
        <MfrLiquidsProvider>
          <MfrSeedsProvider>
            <MfrGTINPrefixesProvider>
              <Box>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" component="h1" sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    mb: 2
                  }}>
                    Manufacturer Management
                  </Typography>
                </Box>
                
                <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTabs-indicator': {
                        height: 4,
                        borderRadius: '4px 4px 0 0',
                      },
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: 120,
                        '&.Mui-selected': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        },
                      },
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    {['Manufacturers', 'Treatment Recipes', 'Seeds', 'Treatment Products', 'GTIN Prefixes'].map(
                      (name, index) => (
                        <Tab key={index} label={name} />
                      )
                    )}
                  </Tabs>
                </Paper>
                <Box>
                  
                  {['Manufacturers', 'Recipes', 'Seeds', 'Liquids', 'GTIN Prefixes'].map((name, index) => (
                    <Box sx={{ pt: 1 }} key={index}>
                      <TabPanel value={activeTab} index={index}>
                        <Paper 
                          elevation={1} 
                          sx={{ 
                            borderRadius: 2, 
                            overflow: 'hidden',
                            mb: 2
                          }}
                        >
                          <CardContent sx={{ p: 0, pt: 0.5 }}>
                            <DataTable
                              title={null}
                              columns={
                                [manufacturerColumns, mfrRecipeColumns, seedColumns, liquidColumns, gtinPrefixColumns][index]
                              }
                              data={displayData[index].map((item: any) => ({
                                ...item,
                                onDelete: handleDeleteClick
                              }))}
                              subHeader
                              subHeaderComponent={
                                <Box 
                                  display="grid" 
                                  gridTemplateColumns="1fr auto 1fr"
                                  alignItems="center"
                                  width="100%" 
                                  mb={1}
                                  gap={1}
                                  py={0.25}
                                  sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 1 }}
                                >
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                                      {name} List
                                    </Typography>
                                  </Box>
                                  <Box sx={{ width: 400, justifySelf: 'center' }}>
                                    <TextField
                                      size="small"
                                      variant="outlined"
                                      placeholder={`Search ${name}...`}
                                      value={searchTerms[index]}
                                      onChange={handleSearchChange(index)}
                                      fullWidth
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '20px',
                                          backgroundColor: 'background.paper',
                                          boxShadow: theme.shadows[1],
                                          '&:hover': {
                                            boxShadow: theme.shadows[2],
                                          },
                                          '&.Mui-focused': {
                                            boxShadow: `${theme.shadows[2]}, 0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                                          },
                                          '& fieldset': {
                                            border: 'none',
                                          },
                                        },
                                      }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ justifySelf: 'end' }}>
                                    <Button 
                                      variant="contained" 
                                      color="secondary" 
                                      onClick={handleAddButtonClick}
                                      startIcon={<AddIcon />}
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        px: 3,
                                        py: 1,
                                        fontWeight: 600,
                                        boxShadow: theme.shadows[2],
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: theme.shadows[4],
                                          transition: 'all 0.2s ease-in-out'
                                        }
                                      }}
                                    >
                                      New {['Manufacturer', 'Treatment Recipe', 'Seed', 'Treatment Product', 'GTIN Prefix'][index]}
                                    </Button>
                                  </Box>
                                </Box>
                              }
                              defaultSortAsc={false}
                              highlightOnHover
                              pointerOnHover
                              customStyles={{
                                headRow: {
                                  style: {
                                    backgroundColor: theme.palette.grey[100],
                                    borderBottomWidth: '1px',
                                    borderBottomColor: theme.palette.divider,
                                    borderBottomStyle: 'solid',
                                    fontSize: '0.875rem',
                                    fontWeight: 900,
                                    color: theme.palette.text.primary,
                                    minHeight: '48px',
                                  },
                                },
                                headCells: {
                                  style: {
                                    paddingLeft: '16px',
                                    paddingRight: '16px',
                                    borderRight: 'none',
                                    borderLeft: 'none',
                                  },
                                },
                                rows: {
                                  style: {
                                    backgroundColor: theme.palette.background.paper,
                                    borderBottomWidth: '1px',
                                    borderBottomColor: theme.palette.divider,
                                    borderBottomStyle: 'solid',
                                    minHeight: '48px',
                                    '&:hover': {
                                      backgroundColor: theme.palette.action.hover,
                                    },
                                    '&:not(:last-of-type)': {
                                      borderBottomStyle: 'solid',
                                      borderBottomWidth: '1px',
                                      borderBottomColor: theme.palette.divider,
                                    },
                                  },
                                },
                                cells: {
                                  style: {
                                    paddingLeft: '16px',
                                    paddingRight: '16px',
                                    fontSize: '1.05rem',
                                    borderRight: 'none',
                                    borderLeft: 'none',
                                  },
                                },
                              }}
                              progressPending={
                                [manufacturersLoading, recipesLoading, seedsLoading, liquidsLoading, gtinPrefixesLoading][index]
                              }
                              pagination
                              onRowClicked={(row) => {
                                handleEditItem(row);
                              }}
                            />
                          </CardContent>
                        </Paper>
                      </TabPanel>
                    </Box>
                  ))}
                  
                  {openDialogType && (
                    <Dialog open={!!openDialogType} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                      <DialogTitle>
                        {isEditing ? `Edit ${currentDialogTitle.replace('New ', '')}` : currentDialogTitle}
                      </DialogTitle>
                      <DialogContent dividers>
                        {renderDialogContent()}
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleCloseDialog} color="secondary">
                          Cancel
                        </Button>
                        <Button type="submit" form="dialog-form" color="primary" variant="contained">
                          {isEditing ? 'Update' : 'Save'}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  )}
                </Box>
              </Box>
              
              <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Alert 
                  onClose={handleCloseSnackbar} 
                  severity={snackbar.severity} 
                  variant="filled"
                  sx={{ width: '100%' }}
                >
                  {snackbar.message}
                </Alert>
              </Snackbar>

              {/* Delete Confirmation Dialog */}
              <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  Confirm Delete
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    {itemToDelete && (
                      <>
                        Are you sure you want to delete <strong>"{itemToDelete.liquid_recipe_name || itemToDelete.liquid_name || itemToDelete.seed_variety_name_mfr || itemToDelete.mfr_name || itemToDelete.name || itemToDelete.prefix || 'this item'}"</strong>? 
                        This action cannot be undone.
                      </>
                    )}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDeleteCancel} color="primary">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeleteConfirm} 
                    color="error" 
                    variant="contained"
                    autoFocus
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </MfrGTINPrefixesProvider>
          </MfrSeedsProvider>
        </MfrLiquidsProvider>
      </MfrRecipesProvider>
    </Box>
  );
};

export default AdminManufacturerListPage;